
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Fingerprint, 
  Settings2, 
  Power, 
  ChevronDown, 
  ShieldCheck,
  Activity,
  Key
} from 'lucide-react';

const ProfileDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { 
      label: 'Identity Profile', 
      icon: <Fingerprint size={16} />, 
      path: user?.role === 'ADMIN' ? '/admin/dashboard' : '/user/profile',
      desc: 'Verification & Bio'
    },
    { 
      label: 'Security Protocol', 
      icon: <Key size={16} />, 
      path: user?.role === 'ADMIN' ? '/admin/change-password' : '/user/change-password',
      desc: 'Rotate Session Keys'
    },
    { 
      label: 'Terminal Settings', 
      icon: <Settings2 size={16} />, 
      path: user?.role === 'ADMIN' ? '/admin/settings' : '/user/settings',
      desc: 'API & Preferences'
    },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-1.5 pl-4 rounded-2xl bg-brand-card border border-brand-border hover:border-brand-primary/40 transition-all duration-300 group shadow-lg active:scale-95"
      >
        <div className="text-right hidden sm:block">
          <p className="text-[10px] font-black text-white uppercase tracking-widest">{user?.name.split(' ')[0]}</p>
          <p className="text-[8px] font-bold text-brand-muted uppercase tracking-tighter">{user?.role}</p>
        </div>
        <div className="w-10 h-10 rounded-xl overflow-hidden border border-white/5 relative">
          <img src={`https://picsum.photos/seed/${user?.email}/80/80`} alt="Avatar" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-primary/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        </div>
        <ChevronDown size={14} className={`text-brand-muted group-hover:text-brand-primary transition-transform duration-500 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-4 w-64 glass-card rounded-[2rem] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden z-[100] animate-in fade-in zoom-in-95 slide-in-from-top-4 duration-300">
          <div className="p-6 border-b border-white/5 bg-white/[0.02]">
            <p className="text-[9px] font-black text-brand-muted uppercase tracking-[0.2em] mb-1">Authenticated As</p>
            <p className="text-sm font-bold text-white truncate">{user?.email}</p>
          </div>

          <div className="p-3">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-white/5 transition-all group"
              >
                <div className="w-10 h-10 rounded-xl bg-brand-dark border border-white/5 flex items-center justify-center text-brand-muted group-hover:text-brand-primary group-hover:border-brand-primary/20 transition-all">
                  {item.icon}
                </div>
                <div className="text-left">
                  <p className="text-[11px] font-black text-white uppercase tracking-wider">{item.label}</p>
                  <p className="text-[9px] text-brand-muted font-medium">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="p-3 mt-1 border-t border-white/5 bg-red-500/[0.02]">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-4 p-4 rounded-2xl hover:bg-red-500/10 transition-all group"
            >
              <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-500 transition-all">
                <Power size={16} />
              </div>
              <div className="text-left">
                <p className="text-[11px] font-black text-red-500 uppercase tracking-widest">Terminate Session</p>
                <p className="text-[9px] text-red-500/50 font-medium">Secure Logout</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
